import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Group, Input, Modal, Text } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import { RichTextEditor, Link } from '@mantine/tiptap';
import { TextEditorToolbar } from '../GeneralComponent/TextEditorToolbar';
import { VisiLoader } from '../GeneralComponent/GeneralComponent';
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';

/*
 * The campaign's own note, opened from the Funnel view.
 *
 * Same record the campaign report keeps, so an edit here shows there too. The campaign is the
 * row that was clicked - there is no campaign picker inside, unlike the campaign report's modal.
 */
export function AggregateCampaignNotes({
  opened,
  onClose,
  campaignId,
  campaignName,
  onSaved,
}: {
  opened: boolean;
  onClose: () => void;
  campaignId: string | null;
  campaignName: string;
  // Lets the caller flip the row's icon without refetching the funnel rows.
  onSaved?: (campaignId: string, hasNote: boolean) => void;
}) {
  const navigate = useNavigate();
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // A late response from a previously opened campaign must not overwrite the current one.
  const requestSeq = useRef(0);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      SubScript,
      Highlight,
      Color,
      TextStyle,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: '',
    onUpdate: ({ editor: e }) => setNoteContent(e.getHTML()),
  });

  useEffect(() => {
    if (!opened || !campaignId) { return; }
    const seq = ++requestSeq.current;
    setLoading(true);
    setNoteTitle('');
    setNoteContent('');
    editor?.commands.clearContent();
    AggregatedReportsAPI
      .getcampaignnote({ visiopt_campaign_id: campaignId })
      .then((response: any) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) { navigate('/logout'); }
        return null;
      })
      .then((data: any) => {
        if (seq !== requestSeq.current) { return; }
        if (data && data.status === 1 && data.data) {
          setNoteTitle(data.data.note_title ?? '');
          setNoteContent(data.data.note ?? '');
          editor?.commands.setContent(data.data.note ?? '', false);
        } else if (data) {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message,
          });
        }
        setLoading(false);
      })
      .catch((e: any) => {
        console.log(e);
        if (seq === requestSeq.current) { setLoading(false); }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, campaignId, editor]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaignId) { return; }
    setSaving(true);
    AggregatedReportsAPI
      .savecampaignnote({
        visiopt_campaign_id: campaignId,
        note_title: noteTitle,
        note: noteContent,
      })
      .then((response: any) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) { navigate('/logout'); }
        return null;
      })
      .then((data: any) => {
        setSaving(false);
        if (data && data.status === 1) {
          notifications.show({
            color: 'green',
            icon: <IconCheck size="1.1rem" />,
            title: 'Success',
            message: data.message,
          });
          if (onSaved) { onSaved(campaignId, Number(data.data?.has_note ?? 0) === 1); }
          onClose();
          return;
        }
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: data ? data.message : 'Could not save the note.',
        });
      })
      .catch((e: any) => {
        console.log(e);
        setSaving(false);
      });
  };

  return (
    <Modal.Root opened={opened} onClose={onClose} size="70%" trapFocus={false} zIndex={1000}>
      <Modal.Overlay />
      <Modal.Content>
        <VisiLoader VisiLoaderVisible={loading} />
        <Modal.Header>
          <Modal.Title>
            <Text weight={700}>CAMPAIGN NOTES</Text>
            <Text size="xs" color="dimmed">{campaignName}</Text>
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSave}>
            <Input.Wrapper label="Note Title:" mb={10}>
              <Input value={noteTitle} onChange={(e) => setNoteTitle(e.currentTarget.value)} />
            </Input.Wrapper>
            <RichTextEditor editor={editor} sx={{ '.ProseMirror': { whiteSpace: 'normal' } }}>
              <TextEditorToolbar />
              <div style={{ height: '300px', overflowY: 'auto' }} onClick={() => editor?.chain().focus().run()}>
                <RichTextEditor.Content />
              </div>
            </RichTextEditor>
            <Text size="xs" color="dimmed" mt={8}>
              This note belongs to the Visiopt campaign, so it is the same note the campaign report shows.
            </Text>
            <Group position="right">
              <Button variant="default" size="sm" mt={10} mb={10} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" size="sm" mt={10} mb={10} loading={saving}>
                Save
              </Button>
            </Group>
          </form>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
